import { bridgeActionTypes } from './constants';

import type {
	BridgeEpisode,
	BridgeEpisodeHint,
	BridgeMap,
	BridgeRoom,
	BridgeTaskDetail,
	BridgeTaskResultModalData,
	LockerAgendaData,
	LockerComputerModalVariant,
} from '../types';

export interface GetEpisodesRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_EPISODES_REQUEST;
}

export interface GetEpisodesSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_EPISODES_SUCCESS;
	payload: { episodes: BridgeEpisode[] };
}

export interface GetEpisodesErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_EPISODES_ERROR;
	payload: { error: string };
}

export interface GetRoomRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_ROOM_REQUEST;
	payload: { roomId?: number };
	[key: string]: unknown;
}

export interface GetRoomSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_ROOM_SUCCESS;
	payload: { room: BridgeRoom };
	[key: string]: unknown;
}

export interface GetRoomErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_ROOM_ERROR;
	payload: { error: string };
	[key: string]: unknown;
}

export interface GetTaskRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_TASK_REQUEST;
	payload: { roomId: number; taskType: string; taskId: number };
	[key: string]: unknown;
}

export interface GetTaskSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_TASK_SUCCESS;
	payload: { task: BridgeTaskDetail };
	[key: string]: unknown;
}

export interface GetTaskErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_TASK_ERROR;
	payload: { error: string };
	[key: string]: unknown;
}

export interface OpenLockerComputerModalAction {
	type: typeof bridgeActionTypes.BRIDGE_OPEN_LOCKER_COMPUTER_MODAL;
	payload: { variant: LockerComputerModalVariant };
	[key: string]: unknown;
}

export interface CloseLockerComputerModalAction {
	type: typeof bridgeActionTypes.BRIDGE_CLOSE_LOCKER_COMPUTER_MODAL;
	[key: string]: unknown;
}

export interface FinishTaskRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_FINISH_TASK_REQUEST;
	payload: { gameOutcome?: string; score?: number; answerIds?: number[] };
	[key: string]: unknown;
}

export interface FinishTaskSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_FINISH_TASK_SUCCESS;
	payload: { hints: BridgeEpisodeHint[]; result: BridgeTaskResultModalData | null };
	[key: string]: unknown;
}

export interface ClearTaskFinishResultAction {
	type: typeof bridgeActionTypes.BRIDGE_CLEAR_TASK_FINISH_RESULT;
	[key: string]: unknown;
}

export interface FinishTaskErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_FINISH_TASK_ERROR;
	payload: { error: string };
	[key: string]: unknown;
}

export interface CheckLockerPasswordRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_REQUEST;
	payload: { episodeId: number; password: string };
	[key: string]: unknown;
}

export interface CheckLockerPasswordSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_SUCCESS;
	payload: { agendaData: LockerAgendaData };
	[key: string]: unknown;
}

export interface CheckLockerPasswordWrongAction {
	type: typeof bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_WRONG;
	payload: { error: string };
	[key: string]: unknown;
}

export interface CheckLockerPasswordErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_ERROR;
	payload: { error: string };
	[key: string]: unknown;
}

export interface OpenLockerAgendaModalAction {
	type: typeof bridgeActionTypes.BRIDGE_OPEN_LOCKER_AGENDA_MODAL;
	[key: string]: unknown;
}

export interface CloseLockerAgendaModalAction {
	type: typeof bridgeActionTypes.BRIDGE_CLOSE_LOCKER_AGENDA_MODAL;
	[key: string]: unknown;
}

export interface CloseRoomDialogAction {
	type: typeof bridgeActionTypes.BRIDGE_CLOSE_ROOM_DIALOG;
	[key: string]: unknown;
}

export interface SendRoomMessageRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_SEND_ROOM_MESSAGE_REQUEST;
	payload: { roomId: number; messageType: string };
	[key: string]: unknown;
}

export interface PrepareGameResumeAction {
	type: typeof bridgeActionTypes.BRIDGE_PREPARE_GAME_RESUME;
	payload: { roomId: number };
	[key: string]: unknown;
}

export interface ClearGameResumeAction {
	type: typeof bridgeActionTypes.BRIDGE_CLEAR_GAME_RESUME;
	[key: string]: unknown;
}

export interface GetMapRequestAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_MAP_REQUEST;
	payload: { roomId: number };
	[key: string]: unknown;
}

export interface GetMapSuccessAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_MAP_SUCCESS;
	payload: { map: BridgeMap };
	[key: string]: unknown;
}

export interface GetMapErrorAction {
	type: typeof bridgeActionTypes.BRIDGE_GET_MAP_ERROR;
	payload: { error: string };
	[key: string]: unknown;
}

export type BridgeAction =
	| GetEpisodesRequestAction
	| GetEpisodesSuccessAction
	| GetEpisodesErrorAction
	| GetRoomRequestAction
	| GetRoomSuccessAction
	| GetRoomErrorAction
	| GetTaskRequestAction
	| GetTaskSuccessAction
	| GetTaskErrorAction
	| OpenLockerComputerModalAction
	| CloseLockerComputerModalAction
	| FinishTaskRequestAction
	| FinishTaskSuccessAction
	| FinishTaskErrorAction
	| CheckLockerPasswordRequestAction
	| CheckLockerPasswordSuccessAction
	| CheckLockerPasswordWrongAction
	| CheckLockerPasswordErrorAction
	| OpenLockerAgendaModalAction
	| CloseLockerAgendaModalAction
	| CloseRoomDialogAction
	| SendRoomMessageRequestAction
	| ClearTaskFinishResultAction
	| PrepareGameResumeAction
	| ClearGameResumeAction
	| GetMapRequestAction
	| GetMapSuccessAction
	| GetMapErrorAction;

export const getEpisodesRequest = (): GetEpisodesRequestAction => ({
	type: bridgeActionTypes.BRIDGE_GET_EPISODES_REQUEST,
});

export const getEpisodesSuccess = (episodes: BridgeEpisode[]): GetEpisodesSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_GET_EPISODES_SUCCESS,
	payload: { episodes },
});

export const getEpisodesError = (error: string): GetEpisodesErrorAction => ({
	type: bridgeActionTypes.BRIDGE_GET_EPISODES_ERROR,
	payload: { error },
});

export const getRoomRequest = (roomId?: number): GetRoomRequestAction => ({
	type: bridgeActionTypes.BRIDGE_GET_ROOM_REQUEST,
	payload: { roomId },
});

export const getRoomSuccess = (room: BridgeRoom): GetRoomSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_GET_ROOM_SUCCESS,
	payload: { room },
});

export const getRoomError = (error: string): GetRoomErrorAction => ({
	type: bridgeActionTypes.BRIDGE_GET_ROOM_ERROR,
	payload: { error },
});

export const getTaskRequest = (
	roomId: number,
	taskType: string,
	taskId: number,
): GetTaskRequestAction => ({
	type: bridgeActionTypes.BRIDGE_GET_TASK_REQUEST,
	payload: { roomId, taskType, taskId },
});

export const getTaskSuccess = (task: BridgeTaskDetail): GetTaskSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_GET_TASK_SUCCESS,
	payload: { task },
});

export const getTaskError = (error: string): GetTaskErrorAction => ({
	type: bridgeActionTypes.BRIDGE_GET_TASK_ERROR,
	payload: { error },
});

export const openLockerComputerModal = (
	variant: LockerComputerModalVariant,
): OpenLockerComputerModalAction => ({
	type: bridgeActionTypes.BRIDGE_OPEN_LOCKER_COMPUTER_MODAL,
	payload: { variant },
});

export const closeLockerComputerModal = (): CloseLockerComputerModalAction => ({
	type: bridgeActionTypes.BRIDGE_CLOSE_LOCKER_COMPUTER_MODAL,
});

export const finishTaskRequest = (
	gameOutcome?: string,
	score?: number,
	answerIds?: number[],
): FinishTaskRequestAction => ({
	type: bridgeActionTypes.BRIDGE_FINISH_TASK_REQUEST,
	payload: { gameOutcome, score, answerIds },
});

export const finishTaskSuccess = (
	hints: BridgeEpisodeHint[],
	result: BridgeTaskResultModalData | null,
): FinishTaskSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_FINISH_TASK_SUCCESS,
	payload: { hints, result },
});

export const clearTaskFinishResult = (): ClearTaskFinishResultAction => ({
	type: bridgeActionTypes.BRIDGE_CLEAR_TASK_FINISH_RESULT,
});

export const finishTaskError = (error: string): FinishTaskErrorAction => ({
	type: bridgeActionTypes.BRIDGE_FINISH_TASK_ERROR,
	payload: { error },
});

export const checkLockerPasswordRequest = (
	episodeId: number,
	password: string,
): CheckLockerPasswordRequestAction => ({
	type: bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_REQUEST,
	payload: { episodeId, password },
});

export const checkLockerPasswordSuccess = (
	agendaData: LockerAgendaData,
): CheckLockerPasswordSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_SUCCESS,
	payload: { agendaData },
});

export const checkLockerPasswordWrong = (error: string): CheckLockerPasswordWrongAction => ({
	type: bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_WRONG,
	payload: { error },
});

export const checkLockerPasswordError = (error: string): CheckLockerPasswordErrorAction => ({
	type: bridgeActionTypes.BRIDGE_CHECK_LOCKER_PASSWORD_ERROR,
	payload: { error },
});

export const openLockerAgendaModal = (): OpenLockerAgendaModalAction => ({
	type: bridgeActionTypes.BRIDGE_OPEN_LOCKER_AGENDA_MODAL,
});

export const closeLockerAgendaModal = (): CloseLockerAgendaModalAction => ({
	type: bridgeActionTypes.BRIDGE_CLOSE_LOCKER_AGENDA_MODAL,
});

export const closeRoomDialog = (): CloseRoomDialogAction => ({
	type: bridgeActionTypes.BRIDGE_CLOSE_ROOM_DIALOG,
});

export const sendRoomMessageRequest = (
	roomId: number,
	messageType: string,
): SendRoomMessageRequestAction => ({
	type: bridgeActionTypes.BRIDGE_SEND_ROOM_MESSAGE_REQUEST,
	payload: { roomId, messageType },
});

export const prepareGameResume = (roomId: number): PrepareGameResumeAction => ({
	type: bridgeActionTypes.BRIDGE_PREPARE_GAME_RESUME,
	payload: { roomId },
});

export const clearGameResume = (): ClearGameResumeAction => ({
	type: bridgeActionTypes.BRIDGE_CLEAR_GAME_RESUME,
});

export const getMapRequest = (roomId: number): GetMapRequestAction => ({
	type: bridgeActionTypes.BRIDGE_GET_MAP_REQUEST,
	payload: { roomId },
});

export const getMapSuccess = (map: BridgeMap): GetMapSuccessAction => ({
	type: bridgeActionTypes.BRIDGE_GET_MAP_SUCCESS,
	payload: { map },
});

export const getMapError = (error: string): GetMapErrorAction => ({
	type: bridgeActionTypes.BRIDGE_GET_MAP_ERROR,
	payload: { error },
});
